
import { useBranding } from '@/contexts/branding-context';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface TenantLogoProps {
  className?: string;
  fallbackClassName?: string;
  showFallbackText?: boolean;
}

/**
 * TenantLogo component that displays the tenant's custom logo or a fallback
 */
export function TenantLogo({
  className,
  fallbackClassName,
  showFallbackText = true,
}: TenantLogoProps) {
  const { logoUrl, fallbackLogoUrl, businessName, loading } = useBranding();

  if (loading) {
    return <Skeleton className={cn('h-10 w-32', className)} />;
  }

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={businessName}
        className={cn('h-10 w-auto object-contain', className)}
        onError={(e) => {
          // Fallback to tenant-specific local logo on error
          (e.target as HTMLImageElement).src = fallbackLogoUrl;
        }}
      />
    );
  }

  // Fallback: show default logo or business name
  if (showFallbackText) {
    return (
      <div className={cn('flex items-center gap-2', fallbackClassName)}>
        <img
          src={fallbackLogoUrl}
          alt="FlexGym Logo"
          className={cn('h-10 w-auto', className)}
        />
      </div>
    );
  }

  return (
    <img
      src={fallbackLogoUrl}
      alt="FlexGym Logo"
      className={cn('h-10 w-auto', className)}
    />
  );
}

/**
 * Small logo variant for compact spaces
 */
export function TenantLogoSmall({ className }: { className?: string }) {
  const { logoUrl, fallbackLogoUrl, businessName, loading } = useBranding();

  if (loading) {
    return <Skeleton className={cn('h-8 w-8 rounded', className)} />;
  }

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={businessName}
        className={cn('h-8 w-auto object-contain', className)}
        onError={(e) => {
          (e.target as HTMLImageElement).src = fallbackLogoUrl;
        }}
      />
    );
  }

  return (
    <img
      src={fallbackLogoUrl}
      alt="FlexGym Logo"
      className={cn('h-8 w-auto', className)}
    />
  );
}
